'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import {
  ArrowLeft, Plane, MapPin, Calendar, Users, DollarSign,
  Image as ImageIcon, Save, Loader2, Plus, X, ChevronRight, Info,
  Video, Link2, ChevronLeft, Trash2, Upload
} from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { parseAuthToken } from '@/utils/base64'
import { compressImage, validateImageFile } from '@/utils/imageCompression'

interface MediaItem {
  type: 'image' | 'video'
  url: string
}

interface TourFormData {
  title: string
  description: string
  thumbnail_url: string
  media_items: MediaItem[]
  tour_type: 'domestic' | 'overseas'
  destination: string
  country: string
  meeting_point: string
  start_date: string
  end_date: string
  min_participants: number
  max_participants: number
  price: number
  early_bird_price: number | null
  early_bird_deadline: string
  required_level: string
  price_includes: string[]
  price_excludes: string[]
  required_gear: string[]
  notes: string
}

export default function TourRegisterPage() {
  const router = useRouter()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  const bg      = isDark ? '#0f172a' : '#f4f7fb'
  const card    = isDark ? '#1e2a3d' : '#ffffff'
  const border  = isDark ? '#2c3a52' : '#e9eff7'
  const ink     = isDark ? '#e8f0fb' : '#0a1f3a'
  const secondary = isDark ? '#94a3b8' : '#6b7684'
  const accent  = isDark ? '#5ea6ff' : '#2b6df0'

  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const [instructorId, setInstructorId] = useState<string | null>(null)
  const [isFirstTour, setIsFirstTour] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [step, setStep] = useState(1)

  const [formData, setFormData] = useState<TourFormData>({
    title: '',
    description: '',
    thumbnail_url: '',
    media_items: [],
    tour_type: 'domestic',
    destination: '',
    country: '',
    meeting_point: '',
    start_date: '',
    end_date: '',
    min_participants: 4,
    max_participants: 8,
    price: 0,
    early_bird_price: null,
    early_bird_deadline: '',
    required_level: '',
    price_includes: [],
    price_excludes: [],
    required_gear: [],
    notes: ''
  })

  const [newVideoUrl, setNewVideoUrl] = useState('')
  const [mediaPreviewIndex, setMediaPreviewIndex] = useState(0)
  const [isUploadingImage, setIsUploadingImage] = useState(false)

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const [newInclude, setNewInclude] = useState('')
  const [newExclude, setNewExclude] = useState('')
  const [newGear, setNewGear] = useState('')
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [pendingTourId, setPendingTourId] = useState<string | null>(null)

  const [priceDisplay, setPriceDisplay] = useState('')
  const [earlyBirdPriceDisplay, setEarlyBirdPriceDisplay] = useState('')

  const formatNumberWithCommas = (value: number | string): string => {
    const num = typeof value === 'string' ? value.replace(/,/g, '') : String(value)
    if (!num || num === '0') return ''
    return Number(num).toLocaleString('ko-KR')
  }

  const parseNumberFromDisplay = (display: string): number => {
    const num = display.replace(/,/g, '')
    return parseInt(num) || 0
  }

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '')
    const numValue = parseInt(value) || 0
    setPriceDisplay(formatNumberWithCommas(value))
    setFormData({ ...formData, price: numValue })
  }

  const handleEarlyBirdPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '')
    const numValue = value ? parseInt(value) : null
    setEarlyBirdPriceDisplay(formatNumberWithCommas(value))
    setFormData({ ...formData, early_bird_price: numValue })
  }

  const getYouTubeEmbedUrl = (url: string): string => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
    const match = url.match(regExp)
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`
    }
    return url
  }

  const getYouTubeThumbnail = (url: string): string | null => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
    const match = url.match(regExp)
    if (match && match[2].length === 11) {
      return `https://img.youtube.com/vi/${match[2]}/hqdefault.jpg`
    }
    return null
  }

  const isValidYouTubeUrl = (url: string): boolean => {
    const patterns = [
      /youtube\.com\/watch/i,
      /youtu\.be\//i,
      /youtube\.com\/embed/i
    ]
    return patterns.some(pattern => pattern.test(url))
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const validation = validateImageFile(file)
    if (!validation.valid) {
      alert(validation.error)
      return
    }

    if (formData.media_items.length >= 3) {
      alert('최대 3개까지 추가할 수 있습니다.')
      return
    }

    setIsUploadingImage(true)

    try {
      const compressed = await compressImage(file, { maxWidth: 1920, maxHeight: 1920, quality: 0.85, maxSizeMB: 5 })

      const supabase = getSupabaseClient()
      const fileExt = compressed.name.split('.').pop()
      const fileName = `tour_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`

      const { error } = await supabase.storage
        .from('tour-images')
        .upload(fileName, compressed, { contentType: compressed.type, upsert: false })

      if (error) throw error

      const { data: urlData } = supabase.storage
        .from('tour-images')
        .getPublicUrl(fileName)

      setFormData({
        ...formData,
        media_items: [...formData.media_items, { type: 'image', url: urlData.publicUrl }]
      })
    } catch (error: any) {
      console.error('Upload error:', error)
      alert('이미지 업로드에 실패했습니다: ' + (error.message || '알 수 없는 오류'))
    } finally {
      setIsUploadingImage(false)
      e.target.value = ''
    }
  }

  const addVideoLink = () => {
    if (!newVideoUrl.trim()) return

    if (!isValidYouTubeUrl(newVideoUrl)) {
      alert('유효한 YouTube 링크를 입력해주세요.')
      return
    }

    if (formData.media_items.length >= 3) {
      alert('최대 3개까지 추가할 수 있습니다.')
      return
    }

    setFormData({
      ...formData,
      media_items: [...formData.media_items, { type: 'video', url: newVideoUrl.trim() }]
    })
    setNewVideoUrl('')
  }

  const getMinEndDate = (): string => {
    if (!formData.start_date || formData.start_date.length < 10) return ''
    const startDate = new Date(formData.start_date)
    if (isNaN(startDate.getTime())) return ''
    startDate.setDate(startDate.getDate() + 1)
    return startDate.toISOString().split('T')[0]
  }

  const formatDateInput = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 8)
    if (digits.length <= 4) return digits
    if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`
  }

  const isCompleteDate = (s: string) => s.length === 10 && !isNaN(new Date(s).getTime())

  const handleStartDateChange = (newStartDate: string) => {
    setFormData(prev => {
      const newState = { ...prev, start_date: newStartDate }
      if (prev.end_date && prev.end_date <= newStartDate) {
        newState.end_date = ''
      }
      return newState
    })
  }

  const removeMedia = (index: number) => {
    const newItems = formData.media_items.filter((_, i) => i !== index)
    setFormData({ ...formData, media_items: newItems })
    if (mediaPreviewIndex >= newItems.length && newItems.length > 0) {
      setMediaPreviewIndex(newItems.length - 1)
    }
  }

  const prevMedia = () => {
    setMediaPreviewIndex(prev =>
      prev === 0 ? formData.media_items.length - 1 : prev - 1
    )
  }
  const nextMedia = () => {
    setMediaPreviewIndex(prev =>
      prev === formData.media_items.length - 1 ? 0 : prev + 1
    )
  }

  const handleDragStart = (e: React.DragEvent | React.TouchEvent, index: number) => {
    setDraggedIndex(index)
    if ('dataTransfer' in e) {
      e.dataTransfer.effectAllowed = 'move'
    }
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index)
    }
  }

  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      const newItems = [...formData.media_items]
      const [draggedItem] = newItems.splice(draggedIndex, 1)
      newItems.splice(dragOverIndex, 0, draggedItem)
      setFormData({ ...formData, media_items: newItems })

      if (mediaPreviewIndex === draggedIndex) {
        setMediaPreviewIndex(dragOverIndex)
      } else if (draggedIndex < mediaPreviewIndex && dragOverIndex >= mediaPreviewIndex) {
        setMediaPreviewIndex(mediaPreviewIndex - 1)
      } else if (draggedIndex > mediaPreviewIndex && dragOverIndex <= mediaPreviewIndex) {
        setMediaPreviewIndex(mediaPreviewIndex + 1)
      }
    }
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const [touchStartX, setTouchStartX] = useState<number | null>(null)
  const [touchCurrentIndex, setTouchCurrentIndex] = useState<number | null>(null)

  const handleTouchStart = (e: React.TouchEvent, index: number) => {
    setTouchStartX(e.touches[0].clientX)
    setTouchCurrentIndex(index)
    setDraggedIndex(index)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX === null || touchCurrentIndex === null) return

    const touch = e.touches[0]
    const elements = document.querySelectorAll('[data-media-thumb]')

    elements.forEach((el, idx) => {
      const rect = el.getBoundingClientRect()
      if (touch.clientX >= rect.left && touch.clientX <= rect.right) {
        if (idx !== touchCurrentIndex) {
          setDragOverIndex(idx)
        }
      }
    })
  }

  const handleTouchEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      const newItems = [...formData.media_items]
      const [draggedItem] = newItems.splice(draggedIndex, 1)
      newItems.splice(dragOverIndex, 0, draggedItem)
      setFormData({ ...formData, media_items: newItems })

      if (mediaPreviewIndex === draggedIndex) {
        setMediaPreviewIndex(dragOverIndex)
      }
    }
    setDraggedIndex(null)
    setDragOverIndex(null)
    setTouchStartX(null)
    setTouchCurrentIndex(null)
  }

  useEffect(() => {
    checkInstructorStatus()
  }, [])

  const checkInstructorStatus = async () => {
    try {
      setIsLoading(true)
      const token = localStorage.getItem('auth_token')

      if (!token) {
        alert('로그인이 필요합니다.')
        router.push('/login')
        return
      }

      const decoded = parseAuthToken(token)
      if (!decoded) {
        router.push('/login')
        return
      }

      const supabase = getSupabaseClient()

      let userQuery = supabase.from('user_profiles').select('id')
      if (decoded.kakao_id) {
        userQuery = userQuery.eq('kakao_id', decoded.kakao_id)
      } else if (decoded.naver_id) {
        userQuery = userQuery.eq('naver_id', decoded.naver_id)
      }

      const { data: userProfile, error: userError } = await userQuery.single()

      if (userError || !userProfile) {
        alert('프로필을 찾을 수 없습니다.')
        router.push('/')
        return
      }

      setCurrentUserId(userProfile.id)

      const { data: instructor, error: instructorError } = await supabase
        .from('instructor_profiles')
        .select('id, is_paid')
        .eq('user_id', userProfile.id)
        .single()

      if (instructorError || !instructor) {
        setInstructorId(null)
      } else {
        setInstructorId(instructor.id)

        const { count } = await supabase
          .from('diving_tours')
          .select('id', { count: 'exact', head: true })
          .eq('instructor_id', instructor.id)
          .not('status', 'in', '("draft","cancelled")')

        setIsFirstTour((count ?? 0) === 0)
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (isDraft: boolean = false) => {
    if (!instructorId) {
      alert('강사 등록이 필요합니다.')
      return
    }

    if (!formData.title.trim()) {
      alert('투어 제목을 입력해주세요.')
      return
    }
    if (!formData.destination.trim()) {
      alert('목적지를 입력해주세요.')
      return
    }
    if (!formData.start_date || !formData.end_date) {
      alert('일정을 선택해주세요.')
      return
    }
    if (formData.price <= 0) {
      alert('가격을 입력해주세요.')
      return
    }

    try {
      setIsSaving(true)
      const supabase = getSupabaseClient()

      let thumbnailUrl = formData.thumbnail_url
      if (!thumbnailUrl && formData.media_items.length > 0) {
        const firstMedia = formData.media_items[0]
        if (firstMedia.type === 'image') {
          thumbnailUrl = firstMedia.url
        } else {
          thumbnailUrl = getYouTubeThumbnail(firstMedia.url) || ''
        }
      }

      const { data, error } = await supabase
        .from('diving_tours')
        .insert({
          instructor_id: instructorId,
          title: formData.title,
          description: formData.description,
          thumbnail_url: thumbnailUrl || null,
          media_items: formData.media_items.length > 0 ? formData.media_items : null,
          tour_type: formData.tour_type,
          destination: formData.destination,
          country: formData.tour_type === 'overseas' ? formData.country : null,
          meeting_point: formData.meeting_point || null,
          start_date: formData.start_date,
          end_date: formData.end_date,
          min_participants: formData.min_participants,
          max_participants: formData.max_participants,
          price: formData.price,
          early_bird_price: formData.early_bird_price || null,
          early_bird_deadline: formData.early_bird_deadline || null,
          required_level: formData.required_level || null,
          price_includes: formData.price_includes.length > 0 ? formData.price_includes : null,
          price_excludes: formData.price_excludes.length > 0 ? formData.price_excludes : null,
          required_gear: formData.required_gear.length > 0 ? formData.required_gear : null,
          notes: formData.notes || null,
          status: isDraft ? 'draft' : (isFirstTour ? 'recruiting' : 'pending_payment'),
          published_at: (!isDraft && isFirstTour) ? new Date().toISOString() : null
        })
        .select('id')
        .single()

      if (error) throw error

      if (isDraft) {
        alert('임시 저장되었습니다.')
        router.push(`/tours/${data.id}`)
      } else if (isFirstTour) {
        setIsFirstTour(false)
        router.push(`/tours/${data.id}?first=1`)
      } else {
        setPendingTourId(data.id)
        setShowPaymentModal(true)
      }
    } catch (error) {
      console.error('등록 실패:', error)
      alert('등록에 실패했습니다.')
    } finally {
      setIsSaving(false)
    }
  }

  const addItem = (type: 'includes' | 'excludes' | 'gear') => {
    if (type === 'includes' && newInclude.trim()) {
      setFormData({ ...formData, price_includes: [...formData.price_includes, newInclude.trim()] })
      setNewInclude('')
    } else if (type === 'excludes' && newExclude.trim()) {
      setFormData({ ...formData, price_excludes: [...formData.price_excludes, newExclude.trim()] })
      setNewExclude('')
    } else if (type === 'gear' && newGear.trim()) {
      setFormData({ ...formData, required_gear: [...formData.required_gear, newGear.trim()] })
      setNewGear('')
    }
  }

  const removeItem = (type: 'includes' | 'excludes' | 'gear', index: number) => {
    if (type === 'includes') {
      setFormData({ ...formData, price_includes: formData.price_includes.filter((_, i) => i !== index) })
    } else if (type === 'excludes') {
      setFormData({ ...formData, price_excludes: formData.price_excludes.filter((_, i) => i !== index) })
    } else if (type === 'gear') {
      setFormData({ ...formData, required_gear: formData.required_gear.filter((_, i) => i !== index) })
    }
  }

  const inputClass = `w-full px-4 py-3 rounded-xl border text-sm ${
    isDark
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-[#2b6df0]'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#2b6df0]'
  } focus:outline-none focus:ring-2 focus:ring-[#2b6df0]/20`

  const labelClass = `block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: bg }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: secondary }} />
      </div>
    )
  }

  if (!instructorId) {
    return (
      <div className="min-h-screen" style={{ background: bg }}>
        <div className="sub-sticky z-20 backdrop-blur-xl" style={{ background: `${card}f0`, borderBottom: `1px solid ${border}` }}>
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center">
            <button onClick={() => router.back()} className="p-2 -ml-2">
              <ArrowLeft className="w-5 h-5" style={{ color: ink }} />
            </button>
            <h1 className="ml-2 font-bold" style={{ color: ink }}>투어 등록</h1>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-6 text-center">
          <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: border }}>
            <Plane className="w-5 h-5" style={{ color: secondary }} />
          </div>
          <h2 className="text-sm font-bold mb-2" style={{ color: ink }}>
            강사 등록이 필요합니다
          </h2>
          <p className="text-[11px] mb-3 leading-relaxed" style={{ color: secondary }}>
            투어를 등록하려면 먼저 강사 프로필을 생성해주세요.
            <br />
            강사 프로필에서 자격증, SNS 링크 등을 등록할 수 있습니다.
          </p>
          <Link
            href="/instructor/register"
            className="inline-flex items-center gap-1 px-4 py-2 text-white text-xs font-medium rounded-xl"
            style={{ background: accent }}
          >
            강사 등록하기
            <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen pb-32" style={{ background: bg }}>
      {/* 헤더 */}
      <div className="sub-sticky z-20 backdrop-blur-xl" style={{ background: `${card}f0`, borderBottom: `1px solid ${border}` }}>
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <button onClick={() => router.back()} className="p-2 -ml-2">
              <ArrowLeft className="w-5 h-5" style={{ color: ink }} />
            </button>
            <h1 className="ml-2 font-bold" style={{ color: ink }}>새 투어 등록</h1>
          </div>
          <div className="text-sm" style={{ color: secondary }}>
            {step}/3 단계
          </div>
        </div>

        <div className="h-1" style={{ background: border }}>
          <div
            className="h-full transition-all"
            style={{ width: `${(step / 3) * 100}%`, background: accent }}
          />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {step === 1 && (
          <div className="space-y-6">
            <div className="rounded-2xl p-4" style={{ background: isDark ? '#1e3a5f22' : '#dbeafe', border: `1px solid ${isDark ? '#1e3a5f' : '#bfdbfe'}` }}>
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 flex-shrink-0" style={{ color: accent }} />
                <div>
                  <h3 className="font-medium mb-1" style={{ color: isDark ? '#93c5fd' : '#1e40af' }}>투어 기본 정보</h3>
                  <p className="text-sm" style={{ color: isDark ? '#93c5fd99' : '#1d4ed8' }}>
                    투어의 제목, 목적지, 일정 등 기본 정보를 입력해주세요.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className={labelClass}>투어 제목 *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className={inputClass}
                placeholder="예: 12월 세부 막탄 프리다이빙 투어"
              />
            </div>

            <div>
              <label className={labelClass}>투어 타입 *</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'domestic', label: '국내', icon: '🇰🇷' },
                  { value: 'overseas', label: '해외', icon: '✈️' },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setFormData({ ...formData, tour_type: option.value as any })}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      formData.tour_type === option.value
                        ? 'border-[#2b6df0] bg-[#2b6df0]/10'
                        : isDark ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-2xl mb-1 block">{option.icon}</span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>목적지 *</label>
                <input
                  type="text"
                  value={formData.destination}
                  onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                  className={inputClass}
                  placeholder="예: 세부 막탄"
                />
              </div>
              {formData.tour_type === 'overseas' && (
                <div>
                  <label className={labelClass}>국가</label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className={inputClass}
                    placeholder="예: 필리핀"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>시작일 *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.start_date}
                  onChange={(e) => handleStartDateChange(formatDateInput(e.target.value))}
                  placeholder="YYYY-MM-DD"
                  maxLength={10}
                  className={`${inputClass} cursor-pointer`}
                />
              </div>
              <div>
                <label className={labelClass}>종료일 *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: formatDateInput(e.target.value) })}
                  placeholder="YYYY-MM-DD"
                  maxLength={10}
                  disabled={!isCompleteDate(formData.start_date)}
                  className={`${inputClass} ${!isCompleteDate(formData.start_date) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                />
                {!isCompleteDate(formData.start_date) && (
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    시작일을 먼저 입력하세요
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className={labelClass}>집결지</label>
              <input
                type="text"
                value={formData.meeting_point}
                onChange={(e) => setFormData({ ...formData, meeting_point: e.target.value })}
                className={inputClass}
                placeholder="예: 인천공항 제2터미널 3층 H카운터"
              />
            </div>

            <div>
              <label className={labelClass}>
                투어 이미지/영상 (최대 3개)
              </label>
              <p className={`text-xs mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                이미지 파일 또는 YouTube 링크를 추가할 수 있습니다. 큰 파일은 자동 압축됩니다. 첫 번째 이미지가 썸네일로 사용됩니다.
              </p>

              {formData.media_items.length > 0 && (
                <div className="relative mb-4">
                  <div className={`relative rounded-xl overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    {formData.media_items[mediaPreviewIndex]?.type === 'video' ? (
                      <div className="aspect-video">
                        <iframe
                          src={getYouTubeEmbedUrl(formData.media_items[mediaPreviewIndex].url)}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <img
                        src={formData.media_items[mediaPreviewIndex]?.url}
                        alt={`미디어 ${mediaPreviewIndex + 1}`}
                        className="w-full h-64 object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/placeholder-image.png'
                        }}
                      />
                    )}

                    <button
                      type="button"
                      onClick={() => removeMedia(mediaPreviewIndex)}
                      className="absolute top-2 right-2 p-2 rounded-full bg-red-500/80 text-white hover:bg-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>

                    {formData.media_items.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={prevMedia}
                          className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          onClick={nextMedia}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </>
                    )}

                    {formData.media_items.length > 1 && (
                      <div className="absolute bottom-2 right-2 px-2 py-1 rounded-full bg-black/60 text-white text-xs">
                        {mediaPreviewIndex + 1}/{formData.media_items.length}
                      </div>
                    )}

                    <div className="absolute top-2 left-2">
                      {formData.media_items[mediaPreviewIndex]?.type === 'video' ? (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/80 text-white text-xs">
                          <Video className="w-3 h-3" /> 영상
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-blue-500/80 text-white text-xs">
                          <ImageIcon className="w-3 h-3" /> 이미지
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-3">
                    <p className={`text-xs mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      드래그하여 순서를 변경하세요. 첫 번째가 대표 이미지입니다.
                    </p>
                    <div
                      className="flex gap-2 overflow-x-auto pb-1"
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                    >
                      {formData.media_items.map((item, idx) => (
                        <div
                          key={idx}
                          data-media-thumb
                          draggable
                          onDragStart={(e) => handleDragStart(e, idx)}
                          onDragOver={(e) => handleDragOver(e, idx)}
                          onDragEnd={handleDragEnd}
                          onTouchStart={(e) => handleTouchStart(e, idx)}
                          onClick={() => setMediaPreviewIndex(idx)}
                          className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all cursor-grab active:cursor-grabbing select-none ${
                            idx === mediaPreviewIndex
                              ? 'border-[#2b6df0] ring-2 ring-[#2b6df0]/30'
                              : dragOverIndex === idx
                                ? 'border-blue-400 scale-105'
                                : isDark ? 'border-gray-700' : 'border-gray-200'
                          } ${draggedIndex === idx ? 'opacity-50 scale-95' : ''}`}
                        >
                          {item.type === 'video' ? (
                            <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                              <Video className="w-6 h-6 text-white" />
                            </div>
                          ) : (
                            <img
                              src={item.url}
                              alt={`썸네일 ${idx + 1}`}
                              className="w-full h-full object-cover pointer-events-none"
                              draggable={false}
                            />
                          )}
                          {idx === 0 && (
                            <div className="absolute bottom-0 left-0 right-0 text-white text-[9px] text-center py-0.5 font-medium" style={{ background: '#2b6df0' }}>
                              대표
                            </div>
                          )}
                          <div className={`absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                            isDark ? 'bg-gray-900/80 text-white' : 'bg-white/90 text-gray-700'
                          }`}>
                            {idx + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {formData.media_items.length < 3 && (
                <div className="space-y-3">
                  <div>
                    <label className={`flex items-center justify-center gap-2 w-full py-3 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
                      isUploadingImage
                        ? 'opacity-50 cursor-not-allowed'
                        : isDark
                          ? 'border-gray-700 hover:border-[#2b6df0] hover:bg-gray-800/50'
                          : 'border-gray-300 hover:border-[#2b6df0] hover:bg-gray-50'
                    }`}>
                      {isUploadingImage ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" style={{ color: accent }} />
                          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            업로드 중...
                          </span>
                        </>
                      ) : (
                        <>
                          <Upload className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                          <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                            이미지 업로드
                          </span>
                          <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            (JPG, PNG, GIF / 자동 압축)
                          </span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleImageUpload}
                        disabled={isUploadingImage}
                        className="hidden"
                      />
                    </label>
                  </div>

                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={newVideoUrl}
                        onChange={(e) => setNewVideoUrl(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addVideoLink())}
                        className={inputClass}
                        placeholder="YouTube 링크 입력..."
                      />
                      <Video className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    </div>
                    <button
                      type="button"
                      onClick={addVideoLink}
                      disabled={!newVideoUrl.trim()}
                      className={`px-4 py-2 rounded-xl font-medium transition-all ${
                        newVideoUrl.trim()
                          ? 'bg-red-500 text-white hover:bg-red-600'
                          : isDark ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}

              {formData.media_items.length >= 3 && (
                <p className={`text-xs mt-2 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                  최대 3개까지 추가할 수 있습니다.
                </p>
              )}
            </div>

            <div>
              <label className={labelClass}>간단 설명</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className={`${inputClass} min-h-[100px]`}
                placeholder="투어에 대한 간단한 소개를 작성해주세요..."
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div className={`rounded-2xl p-4 ${isDark ? 'bg-amber-900/20 border border-amber-700/50' : 'bg-amber-50 border border-amber-100'}`}>
              <div className="flex items-start gap-3">
                <DollarSign className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                <div>
                  <h3 className={`font-medium mb-1 ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>모집 및 가격 설정</h3>
                  <p className={`text-sm ${isDark ? 'text-amber-400/80' : 'text-amber-700'}`}>
                    모집 인원과 참가비를 설정해주세요.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>최소 인원</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.min_participants || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '')
                    setFormData({ ...formData, min_participants: parseInt(value) || 0 })
                  }}
                  onBlur={(e) => {
                    if (!formData.min_participants || formData.min_participants < 1) {
                      setFormData({ ...formData, min_participants: 1 })
                    }
                  }}
                  className={inputClass}
                  placeholder="1"
                />
              </div>
              <div>
                <label className={labelClass}>최대 인원 (정원) *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.max_participants || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '')
                    setFormData({ ...formData, max_participants: parseInt(value) || 0 })
                  }}
                  onBlur={(e) => {
                    if (!formData.max_participants || formData.max_participants < 1) {
                      setFormData({ ...formData, max_participants: 1 })
                    }
                  }}
                  className={inputClass}
                  placeholder="1"
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>참가비 (원) *</label>
              <input
                type="text"
                inputMode="numeric"
                value={priceDisplay}
                onChange={handlePriceChange}
                className={inputClass}
                placeholder="0"
              />
            </div>

            <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <h4 className={`font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>얼리버드 할인 (선택)</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>얼리버드 가격</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={earlyBirdPriceDisplay}
                    onChange={handleEarlyBirdPriceChange}
                    className={inputClass}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className={labelClass}>마감일</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formData.early_bird_deadline}
                    onChange={(e) => setFormData({ ...formData, early_bird_deadline: formatDateInput(e.target.value) })}
                    placeholder="YYYY-MM-DD"
                    maxLength={10}
                    className={`${inputClass} cursor-pointer`}
                  />
                </div>
              </div>
            </div>

            <div>
              <label className={labelClass}>포함 사항</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newInclude}
                  onChange={(e) => setNewInclude(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('includes'))}
                  className={inputClass}
                  placeholder="예: 숙박비, 식비, 장비 렌탈 등"
                />
                <button
                  onClick={() => addItem('includes')}
                  className="px-4 py-2 text-white rounded-xl" style={{ background: '#2b6df0' }}
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <p className={`text-xs mt-1.5 mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                항목 입력 후 <span className="font-medium" style={{ color: accent }}>+ 버튼</span> 또는 <span className="font-medium" style={{ color: accent }}>Enter</span>를 눌러 추가하세요
              </p>
              <div className="flex flex-wrap gap-2">
                {formData.price_includes.map((item, idx) => (
                  <span key={idx} className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'}`}>
                    {item}
                    <button onClick={() => removeItem('includes', idx)}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <label className={labelClass}>불포함 사항</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newExclude}
                  onChange={(e) => setNewExclude(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('excludes'))}
                  className={inputClass}
                  placeholder="예: 항공권, 개인 경비 등"
                />
                <button
                  onClick={() => addItem('excludes')}
                  className="px-4 py-2 text-white rounded-xl" style={{ background: '#2b6df0' }}
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <p className={`text-xs mt-1.5 mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                항목 입력 후 <span className="font-medium" style={{ color: accent }}>+ 버튼</span> 또는 <span className="font-medium" style={{ color: accent }}>Enter</span>를 눌러 추가하세요
              </p>
              <div className="flex flex-wrap gap-2">
                {formData.price_excludes.map((item, idx) => (
                  <span key={idx} className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700'}`}>
                    {item}
                    <button onClick={() => removeItem('excludes', idx)}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div className={`rounded-2xl p-4 ${isDark ? 'bg-purple-900/20 border border-purple-700/50' : 'bg-purple-50 border border-purple-100'}`}>
              <div className="flex items-start gap-3">
                <Info className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                <div>
                  <h3 className={`font-medium mb-1 ${isDark ? 'text-purple-300' : 'text-purple-800'}`}>추가 정보</h3>
                  <p className={`text-sm ${isDark ? 'text-purple-400/80' : 'text-purple-700'}`}>
                    참가 요건 및 주의사항을 입력해주세요.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className={labelClass}>필요 레벨</label>
              <input
                type="text"
                value={formData.required_level}
                onChange={(e) => setFormData({ ...formData, required_level: e.target.value })}
                className={inputClass}
                placeholder="예: AIDA 2 이상"
              />
            </div>

            <div>
              <label className={labelClass}>필요 장비</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newGear}
                  onChange={(e) => setNewGear(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('gear'))}
                  className={inputClass}
                  placeholder="예: 웻슈트, 마스크, 핀 등"
                />
                <button
                  onClick={() => addItem('gear')}
                  className="px-4 py-2 text-white rounded-xl" style={{ background: '#2b6df0' }}
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <p className={`text-xs mt-1.5 mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                항목 입력 후 <span className="font-medium" style={{ color: accent }}>+ 버튼</span> 또는 <span className="font-medium" style={{ color: accent }}>Enter</span>를 눌러 추가하세요
              </p>
              <div className="flex flex-wrap gap-2">
                {formData.required_gear.map((item, idx) => (
                  <span key={idx} className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
                    {item}
                    <button onClick={() => removeItem('gear', idx)}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <label className={labelClass}>참고사항 / 주의사항</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className={`${inputClass} min-h-[120px]`}
                placeholder="취소/환불 정책, 주의사항 등을 작성해주세요..."
              />
            </div>
          </div>
        )}
      </div>

      <div className="sticky above-nav z-20">
        <div className="max-w-md mx-auto backdrop-blur-xl rounded-t-2xl shadow-lg" style={{ background: `${card}f5`, borderTop: `1px solid ${border}` }}>
          <div className="px-4 py-3 flex flex-col gap-2">
            {step === 3 && isFirstTour && (
              <div className="text-center text-xs font-semibold py-1.5 rounded-lg" style={{ background: isDark ? '#1e3a5f' : '#dbeafe', color: isDark ? '#60a5fa' : '#1d4ed8' }}>
                첫 번째 투어 이용료 무료
              </div>
            )}
            <div className="flex gap-3">
              {step > 1 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="flex-1 py-2.5 rounded-xl font-medium text-sm"
                  style={{ background: isDark ? '#2c3a52' : '#f1f5f9', color: ink }}
                >
                  이전
                </button>
              )}
              {step < 3 ? (
                <button
                  onClick={() => setStep(step + 1)}
                  className="flex-1 py-2.5 rounded-xl font-bold text-sm text-white transition-all"
                  style={{ background: accent }}
                >
                  다음
                </button>
              ) : (
                <>
                  <button
                    onClick={() => handleSubmit(true)}
                    disabled={isSaving}
                    className="flex-1 py-2.5 rounded-xl font-medium text-sm disabled:opacity-50"
                    style={{ background: isDark ? '#2c3a52' : '#f1f5f9', color: ink }}
                  >
                    임시 저장
                  </button>
                  <button
                    onClick={() => handleSubmit(false)}
                    disabled={isSaving}
                    className="flex-1 py-2.5 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                    style={{ background: accent }}
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {isFirstTour ? '무료로 등록하기' : '등록하기'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 결제 안내 모달 */}
      {showPaymentModal && pendingTourId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl" style={{ background: card }}>
            <div className="px-6 pt-6 pb-2 text-center">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl" style={{ background: isDark ? '#1e3a5f' : '#dbeafe' }}>
                ✅
              </div>
              <h3 className="text-lg font-bold mb-1" style={{ color: ink }}>투어 정보 저장 완료!</h3>
              <p className="text-sm mb-1" style={{ color: secondary }}>자동화 툴 이용료 <strong style={{ color: accent }}>9,900원</strong>을 이체하면 투어가 즉시 공개됩니다</p>
              <p className="text-xs mb-5" style={{ color: secondary }}>투어 종료까지 추가 수수료 0원 · 예약금 100% 강사님 계좌로</p>
            </div>

            <div className="mx-6 mb-4 rounded-2xl p-4" style={{ background: isDark ? '#2c3a52' : '#f4f7fb', border: `1px solid ${border}` }}>
              <div className="space-y-2.5">
                {[
                  { label: '은행', value: '국민은행' },
                  { label: '계좌번호', value: '7816-0100-151961' },
                  { label: '예금주', value: '김상우' },
                  { label: '금액', value: '9,900원', highlight: true },
                  { label: '이체 메모', value: `이용료_${pendingTourId.slice(0, 8)}` },
                ].map(({ label, value, highlight }) => (
                  <div key={label} className="flex justify-between items-center text-sm">
                    <span style={{ color: secondary }}>{label}</span>
                    <span className="font-semibold" style={{ color: highlight ? accent : ink }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-xs text-center mb-5 px-6" style={{ color: secondary }}>
              이체 확인 후 24시간 내 투어가 공개됩니다.<br />
              이후 투어 종료까지 수수료 없이 운영됩니다.<br />
              문의: 커뮤니티 &gt; 문의게시판
            </p>

            <div className="px-6 pb-6">
              <button
                onClick={() => router.push(`/tours/${pendingTourId}`)}
                className="w-full py-3 rounded-xl font-bold text-sm text-white"
                style={{ background: accent }}
              >
                투어 페이지로 이동 →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
